import { useEffect, useState, useCallback, Component } from "react";
import "@/App.css";

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 text-center gap-6">
        <div className="w-12 h-12 border border-rose-800 flex items-center justify-center">
          <span className="text-rose-500 text-2xl font-bold">!</span>
        </div>
        <div>
          <div className="font-display font-black text-xl tracking-tight mb-2">Ein Fehler ist aufgetreten</div>
          <p className="text-muted-foreground text-sm max-w-sm leading-relaxed mb-1">
            Die Anwendung ist auf ein unerwartetes Problem gestoßen.
          </p>
          <p className="text-zinc-600 text-xs font-mono">{this.state.error?.message}</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="border border-zinc-700 px-6 py-2 text-sm font-display uppercase tracking-wider hover:bg-zinc-800 transition-colors"
        >
          Seite neu laden
        </button>
      </div>
    );
  }
}
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import Dashboard from "./components/Dashboard";
import SetupDetail from "./components/SetupDetail";
import Settings from "./components/Settings";
import Alerts from "./components/Alerts";
import Makro from "./components/Makro";
import AddSetupSheet from "./components/AddSetupSheet";
import StatusUpdateSheet from "./components/StatusUpdateSheet";
import LoginPage from "./components/LoginPage";
import Admin from "./components/Admin";
import ForcePasswordChange from "./components/ForcePasswordChange";
import { AuthProvider, useAuth } from "./context/AuthContext";
import api from "./lib/api";
import { applyTheme, getStoredTheme, toggleTheme } from "./lib/theme";
import { isAttractive } from "./lib/proximity";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

function AppInner() {
  const { user, loading: authLoading, setUser } = useAuth();

  useEffect(() => {
    const theme = getStoredTheme();
    applyTheme(theme);
  }, []);

  const theme = getStoredTheme();

  if (authLoading) {
    return (
      <>
        <Toaster theme={theme} position="top-right" richColors />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="animate-spin text-zinc-500" size={32} />
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Toaster theme={theme} position="top-right" richColors />
        <LoginPage />
      </>
    );
  }

  return (
    <>
      <AppContent />
      {user?.force_password_change && (
        <ForcePasswordChange onDone={() => {
          api.getMe().then(u => setUser(u)).catch(() => {});
        }} />
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backendError, setBackendError] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editSetup, setEditSetup] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  // Watchlist state (lifted from Dashboard)
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("ALLE");
  const [onlyAttractive, setOnlyAttractive] = useState(false);
  const [polling, setPolling] = useState(false);
  const [sort, setSort] = useState("name_asc");

  useEffect(() => { applyTheme(theme); }, [theme]);

  const handleToggleTheme = () => {
    toggleTheme();
    setTheme(getStoredTheme());
  };

  const refresh = useCallback(async () => {
    try {
      const data = await api.listSetups();
      setSetups(data);
      setBackendError(false);
    } catch (e) {
      console.error("Failed to load setups", e);
      setBackendError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  // Auto-select first setup
  useEffect(() => {
    if (!selectedId && setups.length > 0) setSelectedId(setups[0].id);
    if (selectedId && !setups.find(s => s.id === selectedId)) {
      setSelectedId(setups.length > 0 ? setups[0].id : null);
    }
  }, [setups]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePoll = async () => {
    setPolling(true);
    try {
      const r = await api.pollNow();
      toast.success(`${r.setups_polled} Setups geprüft. ${r.new_alerts} neue Alerts.`);
      refresh();
    } catch {
      toast.error("Polling fehlgeschlagen.");
    } finally {
      setPolling(false);
    }
  };

  const filteredSetups = (() => {
    const list = setups.filter(s => {
      if (category !== "ALLE" && s.category !== category) return false;
      if (filter && !s.asset_name.toLowerCase().includes(filter.toLowerCase()) &&
          !s.symbol.toLowerCase().includes(filter.toLowerCase())) return false;
      if (onlyAttractive && !isAttractive(s)) return false;
      return true;
    });
    return [...list].sort((a, b) => {
      switch (sort) {
        case "name_asc":  return a.asset_name.localeCompare(b.asset_name, "de");
        case "name_desc": return b.asset_name.localeCompare(a.asset_name, "de");
        case "price_asc":  return (a.last_price ?? -Infinity) - (b.last_price ?? -Infinity);
        case "price_desc": return (b.last_price ?? -Infinity) - (a.last_price ?? -Infinity);
        case "change_asc":  return (a.day_change_percent ?? -Infinity) - (b.day_change_percent ?? -Infinity);
        case "change_desc": return (b.day_change_percent ?? -Infinity) - (a.day_change_percent ?? -Infinity);
        default: return 0;
      }
    });
  })();

  const attractiveCount = setups.filter(isAttractive).length;
  const categories = ["ALLE", ...Array.from(new Set(setups.map(s => s.category)))];

  const openAdd = () => { setEditSetup(null); setSheetOpen(true); };
  const openEdit = (s) => { setEditSetup(s); setSheetOpen(true); };

  return (
    <div className="App">
      <Toaster theme={theme} position="top-right" richColors />
      <BrowserRouter>
        <Layout
          onAddSetup={openAdd}
          onStatusUpdate={() => setStatusOpen(true)}
          sidebarOpen={sidebarOpen}
          onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
          theme={theme}
          onToggleTheme={handleToggleTheme}
          setups={filteredSetups}
          allSetups={setups}
          selectedId={selectedId}
          onSelect={(id) => { setSelectedId(id); if (window.innerWidth < 1024) setSidebarOpen(false); }}
          filter={filter}
          onFilterChange={setFilter}
          category={category}
          onCategoryChange={setCategory}
          categories={categories}
          onlyAttractive={onlyAttractive}
          onToggleAttractive={() => setOnlyAttractive(v => !v)}
          attractiveCount={attractiveCount}
          polling={polling}
          onPoll={handlePoll}
          sort={sort}
          onSortChange={setSort}
          onDeduplicateDone={() => refresh()}
          backendError={backendError}
          onRetryBackend={refresh}
          watchlistLoading={loading}
        >
          <Routes>
            <Route path="/" element={
              <Dashboard
                setups={filteredSetups}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onAddSetup={openAdd}
                onEditSetup={openEdit}
                onRefresh={refresh}
                loading={loading}
                backendError={backendError}
              />
            } />
            <Route path="/setups/:id" element={<SetupDetail />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/makro" element={<Makro />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </Layout>
        <AddSetupSheet
          open={sheetOpen}
          onOpenChange={(v) => { setSheetOpen(v); if (!v) setEditSetup(null); }}
          editSetup={editSetup}
          onCreated={() => refresh()}
          onUpdated={() => refresh()}
        />
        <StatusUpdateSheet
          open={statusOpen}
          onOpenChange={setStatusOpen}
          onDone={() => refresh()}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
